import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Switch, 
  FormControlLabel, 
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Fade,
  Avatar,
  Collapse,
  Card,
  CardContent
} from '@mui/material';
import {
  Email as EmailIcon,
  DarkMode as DarkModeIcon,
  Timer as TimerIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Notifications as NotificationsIcon,
  Palette as PaletteIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/slices/authSlice';
import PageHeader from '../components/common/PageHeader';

export default function Settings() {
  const user = useSelector(selectUser);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    darkMode: false,
    autoLogout: true,
  });
  const [success, setSuccess] = useState<string | null>(null);
  
  // Обработчик изменения настроек
  const handleSettingChange = (setting: keyof typeof settings) => {
    setSettings({
      ...settings,
      [setting]: !settings[setting],
    });
    
    setSuccess('Настройки успешно сохранены');
    
    // Очистка сообщения об успехе через 3 секунды
    setTimeout(() => {
      setSuccess(null);
    }, 3000);
  };

  const settingItems = [
    {
      id: 'emailNotifications',
      icon: <EmailIcon />,
      primary: 'Уведомления по электронной почте',
      secondary: 'Получать уведомления о передачах данных и других событиях по электронной почте',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#667eea',
    },
    {
      id: 'darkMode',
      icon: <DarkModeIcon />,
      primary: 'Темная тема',
      secondary: 'Использовать темный режим интерфейса',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#4facfe',
    },
    {
      id: 'autoLogout',
      icon: <TimerIcon />,
      primary: 'Автоматический выход',
      secondary: 'Автоматически выходить из системы после 1 часа бездействия',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#f5576c',
    },
  ];
  
  return (
    <Box>
      <PageHeader 
        title="Настройки"
        subtitle="Управление параметрами приложения"
      />
      
      <Collapse in={!!success}>
        <Fade in={!!success}>
          <Alert 
            severity="success" 
            icon={<CheckIcon />}
            sx={{ 
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)',
            }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        </Fade>
      </Collapse>
      
      <Fade in timeout={600}>
        <Paper 
          sx={{ 
            p: 4,
            mb: 3,
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PaletteIcon sx={{ fontSize: 30, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Общие настройки
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Настройте приложение под себя
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <List sx={{ p: 0 }}>
            {settingItems.map((item, index) => (
              <Fade in key={item.id} timeout={400 + index * 100}>
                <ListItem
                  sx={{
                    px: 2,
                    py: 2.5,
                    mb: 1,
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: `${item.color}08`,
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: `${item.color}15`,
                      color: item.color,
                      mr: 2,
                      width: 48,
                      height: 48,
                    }}
                  >
                    {item.icon}
                  </Avatar>
                  <ListItemText 
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {item.primary}
                      </Typography>
                    }
                    secondary={item.secondary}
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      edge="end"
                      checked={settings[item.id as keyof typeof settings]}
                      onChange={() => handleSettingChange(item.id as keyof typeof settings)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: item.color,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: item.color,
                        },
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </Fade>
            ))}
          </List>
        </Paper>
      </Fade>
      
      <Fade in timeout={800}>
        <Paper 
          sx={{ 
            p: 4,
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <InfoIcon sx={{ fontSize: 30, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Информация о приложении
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Детали версии и лицензии
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <List sx={{ p: 0 }}>
            <ListItem
              sx={{
                px: 2,
                py: 2,
                borderRadius: 2,
                mb: 1,
                bgcolor: 'rgba(79, 172, 254, 0.05)',
              }}
            >
              <ListItemText 
                primary={
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#4facfe' }}>
                    Версия приложения
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    1.0.0
                  </Typography>
                }
              />
            </ListItem>
            
            <ListItem
              sx={{
                px: 2,
                py: 2,
                borderRadius: 2,
                mb: 1,
                bgcolor: 'rgba(79, 172, 254, 0.05)',
              }}
            >
              <ListItemText 
                primary={
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#4facfe' }}>
                    Последнее обновление
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    01.05.2025
                  </Typography>
                }
              />
            </ListItem>
            
            <ListItem
              sx={{
                px: 2,
                py: 2,
                borderRadius: 2,
                bgcolor: 'rgba(79, 172, 254, 0.05)',
              }}
            >
              <ListItemText 
                primary={
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#4facfe' }}>
                    Лицензия
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    MIT License
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Paper>
      </Fade>
    </Box>
  );
}